import React, { useState } from 'react';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { day, inr } from '../lib/format.js';
import { parseNumber } from '../lib/numbers.js';
import { ROLES, can } from '../lib/roles.js';
import { Alert, Card, CardSkeleton, Empty, Field, Pill, useAsync } from '../components/ui.jsx';

/** List rate and the floor under it. The floor is what makes rate control possible. */
export default function PriceLists() {
  const session = useSession();
  const entityId = session.entities[0] && session.entities[0].id;
  const lists = useAsync(() => api.get('/api/price-lists'), []);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const canEdit = can(session, ROLES.ADMIN_ACCOUNTS);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Price lists</h1>
          <div className="page-sub">
            A customer's own list wins over the house list. The floor is the rate a counter cannot go below without
            an approval — that is where margin actually leaks.
          </div>
        </div>
        {canEdit && (
          <button className="btn" onClick={() => setCreating(!creating)}>
            {creating ? 'Close' : '＋ New price list'}
          </button>
        )}
      </div>

      <Alert onRetry={() => setError(null)}>{error}</Alert>
      <Alert onRetry={lists.reload}>{lists.error}</Alert>
      {creating && canEdit && <NewList entityId={entityId} onDone={() => { setCreating(false); lists.reload(); }} setError={setError} />}
      {lists.loading && !lists.data && <CardSkeleton lines={3} />}

      {lists.data && (
        <Card pad={lists.data.price_lists.length === 0}>
          {lists.data.price_lists.length === 0 ? (
            <Empty title="No price lists yet">Until one exists, the item's default rate applies and nothing has a floor.</Empty>
          ) : (
            <div className="table-wrap" style={{ margin: 0, padding: 0 }}>
              <table>
                <thead><tr><th>List</th><th>Applies to</th><th>Effective</th>
                  <th className="r">Items</th><th>State</th><th /></tr></thead>
                <tbody>
                  {lists.data.price_lists.map((l) => (
                    <tr key={l.id} className="clickable" onClick={() => setOpen(open === l.id ? null : l.id)}>
                      <td>{l.name}{l.notes && <div className="sub">{l.notes}</div>}</td>
                      <td>{l.party_name || <span className="muted">Everyone (house list)</span>}</td>
                      <td>{day(l.effective_from)}{l.effective_to ? ` – ${day(l.effective_to)}` : ''}</td>
                      <td className="r num">{l.item_count}</td>
                      <td><Pill value={l.is_active ? 'issued' : 'cancelled'} label={l.is_active ? 'active' : 'inactive'} /></td>
                      <td className="r muted" aria-hidden="true">{open === l.id ? '▴' : '▾'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {open && <ListEditor id={open} canEdit={canEdit} onSaved={lists.reload} setError={setError} />}
    </>
  );
}

function NewList({ entityId, onDone, setError }) {
  const parties = useAsync(() => api.get(`/api/parties?entity_id=${entityId}&limit=500`), [entityId]);
  const [name, setName] = useState('');
  const [partyId, setPartyId] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post('/api/price-lists', {
        entity_id: entityId, name: name || 'New price list', party_id: partyId || null, items: [],
      });
      onDone();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Card title="New price list">
      <div className="row">
        <Field label="Name"><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Standard list 2026-27" /></Field>
        <Field label="Applies to" hint="leave blank for everyone">
          <select value={partyId} onChange={(e) => setPartyId(e.target.value)}>
            <option value="">Everyone (house list)</option>
            {parties.data && parties.data.parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      </div>
      <button className="btn" disabled={busy} onClick={save}>Create</button>
    </Card>
  );
}

function ListEditor({ id, canEdit, onSaved, setError }) {
  const session = useSession();
  const list = useAsync(() => api.get(`/api/price-lists/${id}`), [id]);
  const items = useAsync(() => api.get(`/api/items?entity_id=${session.entities[0].id}&limit=500`), []);
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState('');
  const [saved, setSaved] = useState(false);

  React.useEffect(() => { if (list.data) setRows(list.data.items.map((i) => ({ ...i }))); }, [list.data]);
  if (list.loading && !list.data) return <CardSkeleton lines={4} />;
  if (list.error) return <Alert onRetry={list.reload}>{list.error}</Alert>;
  if (!rows) return null;

  const save = async () => {
    setSaved(false);
    const items = [];
    for (const r of rows) {
      const rate = parseNumber(r.rate);
      const minRate = parseNumber(r.min_rate);
      const discount = parseNumber(r.max_discount_pct);
      if (!Number.isFinite(rate) || rate < 0) { setError(`${r.item_name}: the list rate must be a number.`); return; }
      if (Number.isNaN(minRate) || (minRate !== null && minRate < 0)) {
        setError(`${r.item_name}: the floor must be a number, or blank for none.`); return;
      }
      if (Number.isNaN(discount) || (discount !== null && (discount < 0 || discount > 100))) {
        setError(`${r.item_name}: the maximum discount must be between 0 and 100.`); return;
      }
      items.push({ item_id: r.item_id, rate, min_rate: minRate, max_discount_pct: discount ?? 0 });
    }
    try {
      await api.put(`/api/price-lists/${id}/items`, { replace: true, items });
      setSaved(true); list.reload(); onSaved();
    } catch (e) { setError(e.message); }
  };

  const add = () => {
    const item = items.data.items.find((i) => i.id === adding);
    if (!item || rows.some((r) => r.item_id === item.id)) return;
    setRows([...rows, {
      item_id: item.id, item_name: item.name, uom: item.uom,
      rate: item.default_rate || 0, min_rate: '', max_discount_pct: 0,
    }]);
    setAdding('');
  };

  return (
    <Card title={list.data.name} note={list.data.party_name ? `Contracted rates for ${list.data.party_name}` : 'House list'}
      actions={canEdit ? <button className="btn small" onClick={save}>Save rates</button> : null}>
      {saved && <Alert kind="ok">Saved. New orders use these rates from now on; documents already raised are untouched.</Alert>}
      <div className="table-wrap">
        <table>
          <thead><tr><th>Item</th><th className="r" style={{ width: 140 }}>List rate</th>
            <th className="r" style={{ width: 140 }}>Floor</th>
            <th className="r" style={{ width: 150 }}>Max discount %</th><th style={{ width: 52 }} /></tr></thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.item_id}>
                <td>{r.item_name}<div className="sub">{r.uom}</div></td>
                <td className="r"><input className="num" style={{ textAlign: 'right' }} disabled={!canEdit} value={r.rate}
                  onChange={(e) => setRows(rows.map((x, i) => (i === idx ? { ...x, rate: e.target.value } : x)))} /></td>
                <td className="r"><input className="num" style={{ textAlign: 'right' }} disabled={!canEdit} value={r.min_rate ?? ''}
                  placeholder="none"
                  onChange={(e) => setRows(rows.map((x, i) => (i === idx ? { ...x, min_rate: e.target.value } : x)))} /></td>
                <td className="r"><input className="num" style={{ textAlign: 'right' }} disabled={!canEdit} value={r.max_discount_pct}
                  onChange={(e) => setRows(rows.map((x, i) => (i === idx ? { ...x, max_discount_pct: e.target.value } : x)))} /></td>
                <td className="r">{canEdit && <button className="btn small ghost"
                  onClick={() => setRows(rows.filter((_, i) => i !== idx))}>✕</button>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5}><span className="muted">No items on this list yet.</span></td></tr>}
          </tbody>
        </table>
      </div>

      {canEdit && <div className="inline" style={{ marginTop: 12 }}>
        <select value={adding} onChange={(e) => setAdding(e.target.value)} style={{ maxWidth: 320 }}>
          <option value="">Add an item…</option>
          {items.data && items.data.items.filter((i) => !rows.some((r) => r.item_id === i.id))
            .map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <button className="btn secondary" disabled={!adding} onClick={add}>Add</button>
        <span className="card-note">A blank floor means the counter may price it freely.</span>
      </div>}
    </Card>
  );
}
